import React, { useState, useEffect } from "react";
import { 
  Search, 
  RotateCw, 
  SlidersHorizontal, 
  Plus, 
  ChevronDown,
  Eraser,
  Calendar,
  Building2,
  Users2
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { getAllBranches } from "../../../api/hrm/branches/info";
import { getAllDepartments } from "../../../api/hrm/departments/departmentInfo";
import { hasPermission } from "../../../utils/permissionUtils";
import MobileAdvancedFilterSheet from "../../../components/ui/MobileAdvancedFilterSheet";

const TaskReportFilter = ({ onFilterChange, onCreateClick }) => {
  const [advanced, setAdvanced] = useState(false);
  const [branchOptions, setBranchOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [isMobile, setIsMobile] = useState(false);

  const [filters, setFilters] = useState({
    keyword: "",
    branch_id: "",
    department_id: "",
    from_date: "",
    to_date: "",
  });

  // ✅ Định dạng ngày mặc định
  const formatDateLocal = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const initialDates = {
      from_date: formatDateLocal(first),
      to_date: formatDateLocal(last),
    };
    setFilters((prev) => ({ ...prev, ...initialDates }));
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    getAllBranches().then((res) => {
      const branches = res.items || res;
      setBranchOptions(branches.map((b) => ({ value: b.id, label: b.name })));
    }).catch(console.error);

    getAllDepartments().then((res) => {
      const depts = res.items || res;
      setDepartmentOptions(depts.map((d) => ({ value: d.id, label: d.name })));
    }).catch(console.error);
  }, []);

  const updateFilter = (name, value) => {
    const updated = { ...filters, [name]: value };
    setFilters(updated);
    onFilterChange?.(updated);
  };

  const clearFilters = () => {
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const cleared = {
      keyword: "",
      branch_id: "",
      department_id: "",
      from_date: formatDateLocal(first),
      to_date: formatDateLocal(last),
    };
    setFilters(cleared);
    onFilterChange?.(cleared);
  };

  const isFiltering = filters.keyword !== "" || filters.branch_id !== "" || filters.department_id !== "";

  // ================= REUSABLE COMPONENTS =================
  const ActionButton = ({ icon: Icon, onClick, title, active }) => (
    <button
      onClick={onClick}
      title={title}
      className={`h-11 w-11 flex items-center justify-center rounded-xl border transition-all duration-200 
        ${active 
          ? "bg-red-50 border-red-200 text-red-600 shadow-sm" 
          : "bg-white border-gray-200 text-gray-500 hover:border-red-400 hover:text-red-500"
        }`}
    >
      <Icon size={18} />
    </button>
  );

  const FilterSelect = ({ label, value, options, onChange, icon: Icon }) => (
    <div className="flex flex-col gap-1.5 w-full">
      <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1 flex items-center gap-1.5">
        {Icon && <Icon size={12} />} {label}
      </label>
      <div className="relative group">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="appearance-none w-full bg-white border border-gray-200 text-gray-700 py-2.5 px-4 pr-10 rounded-2xl text-sm
                     focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-500/5 transition-all cursor-pointer font-medium shadow-sm"
        >
          <option value="">Tất cả {label}</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <ChevronDown size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none group-hover:text-red-500 transition-colors" />
      </div>
    </div>
  );

  const DateInput = ({ label, value, onChange }) => (
    <div className="flex flex-col gap-1.5 w-full">
      <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1 flex items-center gap-1.5">
        <Calendar size={12} /> {label}
      </label>
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white border border-gray-200 text-gray-700 py-2.5 px-4 rounded-2xl text-sm
                   focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-500/5 transition-all font-medium shadow-sm"
      />
    </div>
  );

  // ================= MOBILE RENDER =================
  if (isMobile) {
    return (
      <div className="mb-6 space-y-4">
        <div className="flex gap-2">
          <div className="relative flex-1 group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors" size={18} />
            <input
              type="text"
              placeholder="Tìm nhân viên, công việc..."
              value={filters.keyword}
              onChange={(e) => updateFilter("keyword", e.target.value)}
              className="w-full bg-white border border-gray-200 pl-11 pr-4 py-3 rounded-2xl focus:outline-none focus:border-red-500 shadow-sm transition-all"
            />
          </div>
          <ActionButton icon={SlidersHorizontal} onClick={() => setAdvanced(!advanced)} active={advanced} />
        </div>

        {/* Nút tạo nổi cho Mobile */}
        <button
          onClick={onCreateClick}
          className="fixed bottom-24 right-5 bg-red-700 text-white w-14 h-14 rounded-full shadow-2xl flex items-center justify-center z-50 active:scale-90 transition-transform"
        >
          <Plus size={28} />
        </button>

        <MobileAdvancedFilterSheet
          open={advanced}
          onClose={() => setAdvanced(false)}
          title="Lọc báo cáo"
          footer={
            <button
              type="button"
              onClick={() => setAdvanced(false)}
              className="w-full py-4 bg-red-600 text-white rounded-2xl font-bold shadow-lg shadow-red-200"
            >
              ÁP DỤNG
            </button>
          }
        >
          <div className="grid grid-cols-1 gap-4">
            <FilterSelect label="Chi nhánh" value={filters.branch_id} options={branchOptions} onChange={(v) => updateFilter("branch_id", v)} icon={Building2} />
            <FilterSelect label="Bộ phận" value={filters.department_id} options={departmentOptions} onChange={(v) => updateFilter("department_id", v)} icon={Users2} />
            <div className="grid grid-cols-2 gap-3">
              <DateInput label="Từ ngày" value={filters.from_date} onChange={(v) => updateFilter("from_date", v)} />
              <DateInput label="Đến ngày" value={filters.to_date} onChange={(v) => updateFilter("to_date", v)} />
            </div>
          </div>
        </MobileAdvancedFilterSheet>
      </div>
    );
  }

  // ================= DESKTOP RENDER =================
  return (
    <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm mb-6 transition-all">
      <div className="flex items-center justify-between gap-4">
        {/* Nhóm tìm kiếm & nút chức năng */}
        <div className="flex items-center gap-3 flex-1">
          <div className="relative w-full max-w-sm group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors" size={18} />
            <input
              type="text"
              placeholder="Tìm theo nhân viên hoặc công việc..."
              value={filters.keyword}
              onChange={(e) => updateFilter("keyword", e.target.value)}
              className="w-full bg-gray-50 border-transparent border-2 focus:bg-white focus:border-red-500 pl-11 pr-4 py-2.5 rounded-2xl outline-none transition-all font-medium text-gray-700"
            />
          </div>

          <ActionButton icon={RotateCw} onClick={clearFilters} title="Làm mới" />
          <ActionButton icon={SlidersHorizontal} onClick={() => setAdvanced(!advanced)} active={advanced} title="Lọc nâng cao" />
          
          {isFiltering && (
             <button onClick={clearFilters} className="text-xs font-bold text-red-500 hover:text-red-700 flex items-center gap-1 uppercase tracking-wider transition-all ml-2">
                <Eraser size={14} /> Xóa lọc
             </button>
          )}
        </div>

        {/* Nhóm hành động chính */}
        <div className="flex items-center gap-3">
          {hasPermission("reports.tasks", "C") && (
            <button
              onClick={onCreateClick}
              className="flex items-center gap-2 px-6 py-2.5 bg-red-700 text-white rounded-full font-bold hover:bg-red-800 shadow-lg shadow-red-100 transition-all active:scale-95 uppercase text-[11px] tracking-wider"
            >
              <Plus size={18} />
              Tạo báo cáo
            </button>
          )}
        </div>
      </div>

      {/* Vùng lọc nâng cao */}
      <AnimatePresence>
        {advanced && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6 mt-6 pt-6 border-t border-gray-100">
              <FilterSelect label="Chi nhánh" value={filters.branch_id} options={branchOptions} onChange={(v) => updateFilter("branch_id", v)} icon={Building2} />
              <FilterSelect label="Bộ phận" value={filters.department_id} options={departmentOptions} onChange={(v) => updateFilter("department_id", v)} icon={Users2} />
              <DateInput label="Từ ngày" value={filters.from_date} onChange={(v) => updateFilter("from_date", v)} />
              <DateInput label="Đến ngày" value={filters.to_date} onChange={(v) => updateFilter("to_date", v)} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TaskReportFilter;